import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import Hero from "../components/Hero";
import ServicesSection from "../components/ServicesSection";

const CAYMAN_IMG = "https://customer-assets.emergentagent.com/job_anglin-consult/artifacts/2qswnbj1_cayman%20excellence%20photo.jpg";

const HomePage = () => {
  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <Hero />

      {/* About Preview Section */}
      <section className="section-padding bg-[#0F172A] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-[#3B4B8C]/10 to-transparent pointer-events-none" />
        
        <div className="section-container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={CAYMAN_IMG}
                  alt="Cayman Islands"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Accent Border */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#E53935] -z-10" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="label-accent mb-4 block">About Us</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
                Cayman Islands
                <br />
                <span className="text-[#E53935]">Excellence</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6 text-lg">
                Anglin Sports Consultancy is a premier consulting firm based in the 
                heart of the Cayman Islands. Led by CEO Collin Anglin, we bring over 
                20 years of expertise in strategic business consulting, sports program 
                development, and brand positioning.
              </p>
              <p className="text-slate-400 leading-relaxed mb-8">
                Our mission is to transform organizations through innovative strategies, 
                exceptional event execution, and partnerships that deliver measurable results.
              </p>
              <Link
                to="/about"
                data-testid="about-preview-link"
                className="btn-secondary inline-flex items-center gap-2"
              >
                Learn More About Us
                <ChevronRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <ServicesSection />

      {/* CTA Section */}
      <section className="section-padding bg-[#020617] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#3B4B8C]/20 via-transparent to-[#E53935]/10 pointer-events-none" />
        
        <div className="section-container relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="label-accent mb-4 block">Ready to Start?</span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-6">
              Let's Elevate Your
              <br />
              <span className="text-[#E53935]">Business Together</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mb-10 text-lg">
              Whether you're launching a new sports program, seeking sponsorship 
              opportunities, or need strategic business guidance — we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="cta-contact-btn"
                className="btn-primary inline-flex items-center gap-2"
              >
                Book a Consultation
                <ChevronRight className="w-5 h-5" />
              </Link>
              <Link
                to="/services"
                data-testid="cta-services-btn"
                className="btn-secondary inline-flex items-center gap-2"
              >
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
